import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createShopCashSettlement } from 'apis/fms';
import LOCALS from 'commons/locals';
import { getShopConfig, SHOP_MAP } from 'constant/shop';
import { useAppSelector } from 'store/hooks';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import type { CashBreakdown } from 'types/fms';
import {
  buildCashSettlementPayload,
  calculateCashBreakdownTotal,
  calculateCashSettlementAmounts,
  normalizeCashBreakdownCount,
} from '../cash-settlement-calculations';
import {
  formatCashSettlementAmount,
  formatInputAmount,
  getCashSettlementCurrencyByShop,
  getCashSettlementCurrencySymbol,
  getCashSettlementDenominations,
  getCashSettlementInputPrecision,
  parseInputAmount as parseCashSettlementInputAmount,
} from '../cash-settlement-currency';
import type { CashCountFormValues } from '../components/CashCountForm';
import type { CashSettlementConfirmSummary } from '../components/CashSettlementConfirmModal';
import {
  CashSettlementDataPatch,
  useCashSettlementData,
} from './use-cash-settlement-data';

type UseCashSettlementPageParams = {
  externalSelectedShopId?: number;
};

const emptyCashBreakdown = (): CashBreakdown => ({
  bills: {},
  coins: {},
});

export const useCashSettlementPage = ({
  externalSelectedShopId,
}: UseCashSettlementPageParams) => {
  const { t } = useTranslation();
  const { username, isLoading, shop } = useAppSelector(selectUserInfo);
  const [hasUserChangedShop, setHasUserChangedShop] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCashBreakdown, setShowCashBreakdown] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedShopId, setSelectedShopId] = useState<number>(
    externalSelectedShopId ?? shop ?? SHOP_MAP.GINZA,
  );
  const [cashData, setCashData] = useState<CashCountFormValues>({
    beginningCash: 0,
    treasuryCashInAmount: 0,
    treasuryCashOutAmount: 0,
    actualCash: 0,
    note: '',
    cashCounter: username || '',
    cashBreakdown: emptyCashBreakdown(),
  });

  const shopOptionList = useMemo(
    () =>
      [
        SHOP_MAP.GINZA,
        SHOP_MAP.HONGKONG,
        SHOP_MAP.SINGAPORE,
        SHOP_MAP.SINGAPORE_ASU_GST,
        SHOP_MAP.SINGAPORE_GX,
        SHOP_MAP.UMEDA,
        SHOP_MAP.NEW_YORK,
      ].map((shopId) => ({
        value: shopId,
        label: getShopConfig(shopId).label,
      })),
    [],
  );

  useEffect(() => {
    if (externalSelectedShopId && selectedShopId !== externalSelectedShopId) {
      setSelectedShopId(externalSelectedShopId);
      return;
    }
    if (
      !externalSelectedShopId &&
      shop &&
      !hasUserChangedShop &&
      selectedShopId !== shop
    ) {
      setSelectedShopId(shop);
    }
  }, [externalSelectedShopId, selectedShopId, shop, hasUserChangedShop]);

  const currencyType = getCashSettlementCurrencyByShop(selectedShopId);
  const currencySymbol = getCashSettlementCurrencySymbol(currencyType);
  const amountPrecision = getCashSettlementInputPrecision(currencyType);
  const currentDenominations = getCashSettlementDenominations(currencyType);
  const formatAmount = useCallback(
    (amount: number | null | undefined) =>
      formatCashSettlementAmount(amount, currencyType),
    [currencyType],
  );
  const parseInputAmount = useCallback(
    (value: string | undefined) =>
      parseCashSettlementInputAmount(value, currencyType),
    [currencyType],
  );
  const handleCashDataChange = useCallback((patch: CashSettlementDataPatch) => {
    setCashData((prev) => ({
      ...prev,
      ...patch,
    }));
  }, []);

  const settlementData = useCashSettlementData({
    isUserLoading: isLoading,
    selectedShopId,
    username,
    onCashDataChange: handleCashDataChange,
    onCashBreakdownVisibilityChange: setShowCashBreakdown,
  });
  const settlementAmounts = useMemo(
    () =>
      calculateCashSettlementAmounts({
        beginningCash: cashData.beginningCash,
        actualCash: cashData.actualCash,
        saleCashPayments: settlementData.cashOrderPayments,
        recycledConsignmentCashPayments: settlementData.cashPaymentSummaryList,
        treasuryCashInAmount: cashData.treasuryCashInAmount,
        treasuryCashOutAmount: cashData.treasuryCashOutAmount,
        refundPayments: settlementData.refundOrderPayments,
        paymentAccountList: settlementData.paymentAccountList,
      }),
    [
      cashData.actualCash,
      cashData.beginningCash,
      cashData.treasuryCashInAmount,
      cashData.treasuryCashOutAmount,
      settlementData.cashOrderPayments,
      settlementData.cashPaymentSummaryList,
      settlementData.refundOrderPayments,
      settlementData.paymentAccountList,
    ],
  );
  const cashBreakdownTotal = useMemo(
    () => calculateCashBreakdownTotal(cashData.cashBreakdown),
    [cashData.cashBreakdown],
  );

  useEffect(() => {
    if (showCashBreakdown) {
      setCashData((prev) => ({ ...prev, actualCash: cashBreakdownTotal }));
    }
  }, [cashBreakdownTotal, showCashBreakdown]);

  const handleShopChange = (shopId: number) => {
    setSelectedShopId(shopId);
    setHasUserChangedShop(true);
  };
  const handleCashBreakdownChange = (
    type: 'bills' | 'coins',
    denomination: string,
    count: number | null,
  ) => {
    const newCount = normalizeCashBreakdownCount(count);

    setCashData((prev) => ({
      ...prev,
      cashBreakdown: {
        ...prev.cashBreakdown,
        [type]: {
          ...prev.cashBreakdown[type],
          [denomination]: newCount,
        },
      },
    }));
  };
  const handleTreasuryCashChange = (
    field: 'treasuryCashInAmount' | 'treasuryCashOutAmount',
    value: number | null,
  ) => {
    setCashData((prev) => ({ ...prev, [field]: value === null ? 0 : value }));
  };
  const handleSubmit = () => {
    if (
      cashData.treasuryCashInAmount > 0 &&
      cashData.treasuryCashOutAmount > 0
    ) {
      alert(t(LOCALS.daily_register_close_cash_movement_direction_error));
      return;
    }

    setShowConfirmModal(true);
  };
  const handleConfirmSubmit = async () => {
    if (submitting) {
      return;
    }

    try {
      setSubmitting(true);
      await createShopCashSettlement(
        buildCashSettlementPayload({
          shopId: selectedShopId,
          cashData,
          saleCashPayments: settlementData.cashOrderPayments,
          recycledConsignmentCashPayments:
            settlementData.cashPaymentSummaryList,
          nonCashPayments: settlementData.creditCardPayments,
          recycleStats: settlementData.recycleOrderDailyStats,
          refundOrderPayments: settlementData.refundOrderPayments,
          paymentAccountList: settlementData.paymentAccountList,
          includeCashBreakdown: showCashBreakdown,
        }),
      );
      setShowConfirmModal(false);
      alert(t(LOCALS.daily_register_close_complete_message));
      setCashData((prev) => ({
        ...prev,
        beginningCash: 0,
        treasuryCashInAmount: 0,
        treasuryCashOutAmount: 0,
        actualCash: 0,
        note: '',
        cashBreakdown: emptyCashBreakdown(),
      }));
      setShowCashBreakdown(false);
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  const confirmSummary: CashSettlementConfirmSummary = {
    beginningCash: cashData.beginningCash,
    treasuryCashInAmount: cashData.treasuryCashInAmount,
    treasuryCashOutAmount: cashData.treasuryCashOutAmount,
    treasuryCashMovementNet: settlementAmounts.treasuryCashMovementNet,
    systemCashSales: settlementAmounts.systemCashSales,
    expectedCash: settlementAmounts.expectedCash,
    actualCash: cashData.actualCash,
    variance: settlementAmounts.variance,
    note: cashData.note,
    cashCounter: cashData.cashCounter,
  };

  return {
    amountPrecision,
    cashBreakdownTotal,
    cashData,
    confirmSummary,
    currentDenominations,
    currencySymbol,
    formatAmount,
    formatInputAmount,
    handleCashBreakdownChange,
    handleConfirmSubmit,
    handleShopChange,
    handleSubmit,
    handleTreasuryCashChange,
    parseInputAmount,
    selectedShopId,
    setCashData,
    setShowCashBreakdown,
    setShowConfirmModal,
    setShowHistoryModal,
    settlementAmounts,
    settlementData,
    shopOptionList,
    showCashBreakdown,
    showConfirmModal,
    showHistoryModal,
    submitting,
    submitDisabled:
      cashData.actualCash < 0 ||
      cashData.beginningCash < 0 ||
      !cashData.cashCounter ||
      submitting,
  };
};
